import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";

/**
 * Reformity theme system.
 *
 * Three persisted modes stored as `reformity_theme`:
 *   - "light"  → always light
 *   - "dark"   → always dark
 *   - "system" → follow OS prefers-color-scheme (default)
 *
 * Tailwind is configured with `darkMode: ["class"]`, so we toggle the
 * `.dark` class on <html> based on the resolved theme. The `resolvedTheme`
 * value is always either "light" or "dark" — never "system" — so it can be
 * passed straight to libraries like sonner that need a concrete value.
 *
 * The cycle order (used by the toggle button) is light → dark → system.
 */

const STORAGE_KEY = "reformity_theme";
const VALID = new Set(["light", "dark", "system"]);

function readStored() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return VALID.has(v) ? v : "system";
  } catch {
    return "system";
  }
}

function systemPrefersDark() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function resolve(theme) {
  if (theme === "system") return systemPrefersDark() ? "dark" : "light";
  return theme;
}

function applyClass(resolved) {
  const root = document.documentElement;
  if (resolved === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
  // Also expose data-theme for any non-Tailwind hooks (charts, etc.)
  root.setAttribute("data-theme", resolved);
}

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(() => readStored());
  const [resolvedTheme, setResolvedTheme] = useState(() => resolve(readStored()));

  // Apply class on mount + whenever theme changes
  useEffect(() => {
    const r = resolve(theme);
    setResolvedTheme(r);
    applyClass(r);
  }, [theme]);

  // Subscribe to OS scheme changes ONLY when in system mode
  useEffect(() => {
    if (theme !== "system") return undefined;
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => {
      const r = mq.matches ? "dark" : "light";
      setResolvedTheme(r);
      applyClass(r);
    };
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, [theme]);

  const setTheme = useCallback((next) => {
    if (!VALID.has(next)) return;
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
    setThemeState(next);
  }, []);

  const cycleTheme = useCallback(() => {
    setThemeState((prev) => {
      const order = ["light", "dark", "system"];
      const i = order.indexOf(prev);
      const next = order[(i + 1) % order.length];
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ theme, resolvedTheme, setTheme, cycleTheme }),
    [theme, resolvedTheme, setTheme, cycleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
